const INTERNAL_LOGIN_PATH = '/api/gateway/auth/login'
const ATTRIBUTION_COOKIE_NAME = 'foxia_utm_info'
const LEGACY_ATTRIBUTION_COOKIE_NAME = 'foxia_attribution'
const ATTRIBUTION_COOKIE_MAX_AGE = 60 * 60 * 24 * 30
const SHARED_COOKIE_DOMAIN = '.foxia.vn'
const ATTRIBUTION_QUERY_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const

function normalizeExternalBaseUrl(rawUrl: string | undefined): string | null {
  if (!rawUrl) {
    return null
  }

  const trimmed = rawUrl.trim()
  if (!trimmed) {
    return null
  }

  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`

  try {
    const url = new URL(withScheme)
    return `${url.origin}${url.pathname}`.replace(/\/+$/, '')
  } catch {
    return null
  }
}

export function getCrmOrigin(): string {
  const env = import.meta.env as Record<string, string | undefined>
  const configuredBaseUrl = normalizeExternalBaseUrl(env.VITE_CRM_BASE_URL)

  if (configuredBaseUrl) {
    return new URL(configuredBaseUrl).origin
  }

  return typeof window === 'undefined' ? 'http://localhost' : window.location.origin
}

function readCookieValue(name: string): string | null {
  if (typeof document === 'undefined') {
    return null
  }

  const cookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))

  if (!cookie) {
    return null
  }

  return cookie.slice(name.length + 1)
}

function resolveCookieDomain(): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  const hostname = window.location.hostname.toLowerCase()
  if (hostname === 'foxia.vn' || hostname.endsWith('.foxia.vn')) {
    return SHARED_COOKIE_DOMAIN
  }

  return null
}

function readAttributionFromCookie(): Record<string, string> {
  const rawValue =
    readCookieValue(ATTRIBUTION_COOKIE_NAME) ||
    readCookieValue(LEGACY_ATTRIBUTION_COOKIE_NAME)
  if (!rawValue) {
    return {}
  }

  try {
    const params = new URLSearchParams(decodeURIComponent(rawValue))
    return Object.fromEntries(
      ATTRIBUTION_QUERY_KEYS.map((key) => {
        const value = params.get(key)
        return [key, value ?? ''] as const
      }).filter(([, value]) => Boolean(value)),
    ) as Record<string, string>
  } catch {
    return {}
  }
}

function readAttributionFromCurrentUrl(): Record<string, string> {
  if (typeof window === 'undefined') {
    return {}
  }

  const currentUrl = new URL(window.location.href)
  return Object.fromEntries(
    ATTRIBUTION_QUERY_KEYS.map((key) => {
      const value = currentUrl.searchParams.get(key)
      return [key, value ?? ''] as const
    }).filter(([, value]) => Boolean(value)),
  ) as Record<string, string>
}

function persistAttributionCookie(attribution: Record<string, string>) {
  if (typeof document === 'undefined' || Object.keys(attribution).length === 0) {
    return
  }

  const params = new URLSearchParams()
  Object.entries(attribution).forEach(([key, value]) => {
    if (value) {
      params.set(key, value)
    }
  })

  document.cookie = [
    `${ATTRIBUTION_COOKIE_NAME}=${encodeURIComponent(params.toString())}`,
    `Max-Age=${ATTRIBUTION_COOKIE_MAX_AGE}`,
    'Path=/',
    'SameSite=Lax',
    ...(resolveCookieDomain() ? [`Domain=${resolveCookieDomain()}`] : []),
  ].join('; ')
}

function getEffectiveAttribution(): Record<string, string> {
  const cookieAttribution = readAttributionFromCookie()
  const currentAttribution = readAttributionFromCurrentUrl()
  const attribution = {
    ...cookieAttribution,
    ...currentAttribution,
  }

  if (Object.keys(attribution).length > 0) {
    persistAttributionCookie(attribution)
  }

  return attribution
}

function appendAttributionParams(rawUrl: string): string {
  if (typeof window === 'undefined') {
    return rawUrl
  }

  const attribution = getEffectiveAttribution()
  const currentUrl = new URL(window.location.href)
  const nextUrl = new URL(rawUrl, currentUrl.origin)

  ATTRIBUTION_QUERY_KEYS.forEach((key) => {
    const value = attribution[key]
    if (value && !nextUrl.searchParams.has(key)) {
      nextUrl.searchParams.set(key, value)
    }
  })

  if (/^https?:\/\//i.test(rawUrl)) {
    return nextUrl.toString()
  }

  return `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`
}

export function getCrmLoginUrl(returnTo?: string): string {
  const loginUrl = new URL(INTERNAL_LOGIN_PATH, getCrmOrigin())
  if (returnTo) {
    loginUrl.searchParams.set('return_to', returnTo)
  }

  return appendAttributionParams(loginUrl.toString())
}
